import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, MapPin, Trophy, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Championships = () => {
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChampionships();
  }, []);

  const fetchChampionships = async () => {
    try {
      const response = await axios.get(`${API}/championships`);
      setChampionships(response.data);
    } catch (error) {
      console.error('Error fetching championships:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Hero */}
      <section className="bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4" data-testid="championships-title">Campeonatos</h1>
          <p className="text-xl">
            Participação em torneios nacionais e internacionais de prestígio
          </p>
        </div>
      </section>

      {/* Championships Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="animate-spin h-12 w-12 text-red-600" />
            </div>
          ) : (
            <div className="space-y-8">
              {championships.map((championship) => (
                <Card key={championship.id} className="hover:shadow-xl transition" data-testid={`championship-card-${championship.id}`}>
                  <CardContent className="p-0">
                    <div className="md:flex">
                      {/* Image */}
                      <div className="md:w-1/3 bg-gray-200">
                        <img 
                          src={championship.photo_url} 
                          alt={championship.name}
                          className="w-full h-full object-contain p-8"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=600';
                          }}
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="md:w-2/3 p-8">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">{championship.name}</h2>
                        
                        <div className="space-y-3 mb-4">
                          <div className="flex items-center text-gray-600">
                            <Calendar className="mr-2 text-red-600" size={20} />
                            <span className="font-semibold">Ano: {championship.year}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <MapPin className="mr-2 text-red-600" size={20} />
                            <span>{championship.location}</span>
                          </div>
                          {championship.results && (
                            <div className="flex items-center text-gray-600">
                              <Trophy className="mr-2 text-red-600" size={20} />
                              <span className="font-semibold">{championship.results}</span>
                            </div>
                          )}
                        </div>
                        
                        {championship.description && (
                          <p className="text-gray-700 leading-relaxed">{championship.description}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Galeria Internacional */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Pequeninos do Jockey Internacional
          </h2>
          <p className="text-xl text-center text-gray-600 mb-8">
            Nossos garotos descobrindo o mundo através do esporte
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              'https://www.pequeninos.com.br/wp-content/uploads/5.jpg',
              'https://www.pequeninos.com.br/wp-content/uploads/6.jpg',
              'https://www.pequeninos.com.br/wp-content/uploads/4.jpg',
              'https://www.pequeninos.com.br/wp-content/uploads/3.jpg',
              'https://www.pequeninos.com.br/wp-content/uploads/ggg.jpg',
              'https://www.pequeninos.com.br/wp-content/uploads/8.jpg',
              'https://www.pequeninos.com.br/wp-content/uploads/7.jpg',
              'https://www.pequeninos.com.br/wp-content/uploads/2.jpg'
            ].map((img, idx) => (
              <div key={idx} className="aspect-video overflow-hidden rounded-lg shadow-md hover:shadow-xl transition">
                <img 
                  src={img} 
                  alt={`Campeonato ${idx + 1}`}
                  className="w-full h-full object-cover hover:scale-110 transition duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Championships;